import { notify } from '@wyz/ui';
import { useOnErrorQuery } from '@wyz/utils';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useApplicationContext } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { usePurchaseErrorTranslation } from '~/common/hooks';
import { useManageSearch } from '~/purchase/api';
import { IBaseSearchFormSchema, ISearchTypeArgs } from '~/purchase/types';

type Args = {
  searchType: ISearchTypeArgs['searchType'];
  customer: IUserCustomer;
};
export function useSearchTrigger<TTyreOffer>({ searchType, customer }: Args) {
  const { resolveErrorKey } = usePurchaseErrorTranslation();
  const { application } = useApplicationContext();
  const methods = useFormContext<IBaseSearchFormSchema>();
  const watchedValues = useWatch({
    control: methods.control,
  }) as IBaseSearchFormSchema;

  const { count } = useManageSearch<TTyreOffer>({
    context: { customerId: customer.id, searchType },
    count: {
      args: watchedValues,
      enabled:
        Boolean(watchedValues.identifier?.value) ||
        (Boolean(watchedValues.width) &&
          Boolean(watchedValues.height) &&
          Boolean(watchedValues.diameter)),
    },
  });

  useOnErrorQuery(count, (error) => {
    if (error?.message) {
      notify.error(<Trans i18nKey={resolveErrorKey(error.message)} />, {
        id: 'toast-error-search-count',
      });
    }
  });

  const canSearch = Boolean(
    count.isSuccess &&
    count.data.data &&
    count.data.data <= application.customerSettings.maxTyreSearchResult,
  );

  const maxSearchExceeded =
    count.isSuccess &&
    count.data?.data > application.customerSettings.maxTyreSearchResult;

  const hasNoResults =
    count.isSuccess && !count.isProcessing && count.data?.data === 0;

  return {
    canSearch,
    errors: {
      maxSearchExceeded,
      hasNoResults,
    },
    count,
  };
}
